import React, { FC } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from './input-summary.module.scss';
import MinusIcon from '@app/shared/icons/MinusIcon';
import SvgPlus from '@app/shared/icons/Plus';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { enteredPercentageViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { OpmDlomPerInstrumentPerTranche } from '@app/shared/models/contracts/opm-calculation-results-dto';

export type SortedInstrument = OpmDlomPerInstrumentPerTranche & { instrumentId: string };

interface InputSummaryDlocSectionProps {
  show: boolean | undefined;
  sortedInstruments: SortedInstrument[];
  dlocByInstrument: Dictionary<number> | undefined;
  dloc: number;
  showAll: boolean;
  setShowAll: (value: boolean) => void;
}

export const InputSummaryDlocSection: FC<InputSummaryDlocSectionProps> = ({
  show,
  sortedInstruments,
  dlocByInstrument,
  dloc,
  showAll,
  setShowAll,
}) => {
  const { l } = useLocale();

  if (!show) {
    return (
      <tr>
        <td className={styles['title']}>{l('_DLOC')}</td>
        <td data-testid="dloc">
          <CellValue
            className={`${styles['value']}`}
            value={getCalculatedProjectValueInPercent(dloc)}
            {...enteredPercentageViewRules}
          />
        </td>
      </tr>
    );
  }

  const trancheCount = sortedInstruments.length;
  const showDlocExpander = trancheCount > 2;

  return (
    <>
      <tr>
        <th className={styles['title']} data-testid="dloc-header">
          {l('_DLOC')}
        </th>
      </tr>
      {sortedInstruments.slice(0, showAll ? undefined : 2).map((instrument, mainIndex) => {
        const shouldDisplayDlocExpand = mainIndex === 1 && showDlocExpander;
        const dlocValue = dlocByInstrument?.[instrument.instrumentId];

        return (
          <React.Fragment key={`dloc-fragment-${mainIndex}`}>
            <tr key={`dloc-${instrument.instrumentId}`}>
              <td
                className={`${
                  shouldDisplayDlocExpand
                    ? styles['expand-title-primary']
                    : styles['expand-title-primary-light']
                } ${styles['title']} ${styles['no-border']}`}>
                {instrument.instrumentNarrative}
              </td>
              <td
                className={`${
                  shouldDisplayDlocExpand
                    ? styles['expand-value-primary']
                    : styles['expand-value-primary-light']
                } ${styles['no-border']} table-primary__cell--right`}
                data-testid={`${instrument.instrumentId}-instrument-dloc`}>
                <CellValue
                  value={getCalculatedProjectValueInPercent(dlocValue)}
                  className={`${styles['value']}`}
                  {...enteredPercentageViewRules}
                  alignment={Alignment.Right}
                />
              </td>
            </tr>
            {shouldDisplayDlocExpand && (
              <div className={styles['expander']}>
                <button
                  aria-label={l('_ToggleSectionCollapse', { section: l('_DLOC') })}
                  aria-expanded={showAll}
                  onClick={() => setShowAll(!showAll)}
                  className={styles['expander-button']}
                  data-testid="expander-button-dloc-instrument">
                  {showAll ? (
                    <MinusIcon data-testid="minus-icon-dloc-instrument" />
                  ) : (
                    <SvgPlus data-testid="svg-plus-dloc-instrument" />
                  )}
                </button>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
